import React, { useState } from 'react';
import classNames from 'classnames';

import { API } from 'constants';
import client from '../../../../services/organization-api';

import { defaultErrorMessage } from '../../../../constants/errorMessages';
import { CONNECTION_TYPE, providersCardData } from '../../../../constants/organizationConnections';

import ErrorAlert from '../../../../design-system/ErrorAlert/ErrorAlert';
import AddLineIcon from '../../../../design-system/Icons/AddLineIcon';
import CheckLineIcon from '../../../../design-system/Icons/CheckLineIcon';
import RefreshCwIcon from '../../../../design-system/Icons/RefreshCwIcon';
import { Button, ButtonIcon } from '../../../../design-system';

const ProviderCard = ({
    provider,
    connectionData,
    setConnections,
    hasCreateAccess,
    comingSoon = false,
    setConnectionActionModal,
    oauthUrl = null,
}) => {
    const { logoPath, title, description, connectionType } = providersCardData[provider] || {};

    const [isRefreshLoading, setIsRefreshLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);

    const handleCreateConnection = () => {
        // If oauthUrl is provided, redirect to OAuth page
        if (oauthUrl) {
            window.location.href = oauthUrl; // Redirect user to OAuth URL
        } else {
            // Otherwise, open the connection action modal
            setConnectionActionModal({ opened: true, action: 'create', provider });
        }
    };

    const handleEditConnection = () => {
        setConnectionActionModal({ opened: true, action: 'edit', data: connectionData, provider });
    };

    const handleRefreshConnection = async () => {
        if (oauthUrl && connectionType === CONNECTION_TYPE.oauth) {
            window.location.href = oauthUrl; // Redirect user to OAuth URL
            return;
        }

        try {
            setIsRefreshLoading(true);

            const { id, name } = connectionData;
            const { data } = await client.patch(`${API.ROUTES.organization.connection}${id}/`, {
                name,
            });
            setConnections &&
                setConnections((prevConnections) =>
                    prevConnections.map((connection) =>
                        connection.id === data.id ? data : connection
                    )
                );
        } catch (e) {
            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        } finally {
            setIsRefreshLoading(false);
        }
    };

    const withViewButton = hasCreateAccess && !!connectionData && !comingSoon;
    const containerClassNames = classNames(
        'flex flex-col gap-2.5 items-center min-h-[82px] max-h-[103px] flex-grow',
        {
            'justify-end': withViewButton,
            'justify-center': !withViewButton,
        }
    );

    return (
        <div className="w-full h-[400px] px-8 py-[40px] rounded-2 bg-neutral-50 flex flex-col gap-1.5 items-center justify-between">
            <div className="flex flex-col gap-3 items-center">
                <img
                    src={logoPath}
                    width={100}
                    height={100}
                    alt={`${title} logo`}
                    className="max-h-[100px]"
                />

                <h2 className={`font-heading-bold text-heading-bold-l text-neutral-500`}>
                    {title}
                </h2>
                <p className={`font-body text-body-regular-xs text-neutral-500 text-center`}>
                    {description}
                </p>
            </div>

            <div className={containerClassNames}>
                {comingSoon ? (
                    <Button type="primary" size="xs" text="Coming Soon" state="disabled" />
                ) : connectionData ? (
                    <Button type="secondary" size="xs" text="Active" trailingIcon={CheckLineIcon} />
                ) : (
                    <Button
                        type="primary"
                        size="xs"
                        text="Connect"
                        trailingIcon={AddLineIcon}
                        state={hasCreateAccess ? 'default' : 'disabled'}
                        onClick={handleCreateConnection}
                    />
                )}

                {withViewButton && (
                    <div className="flex items-center gap-2.5">
                        <Button
                            type="link"
                            size="xs"
                            text="View Details"
                            onClick={handleEditConnection}
                        />

                        <ButtonIcon
                            type="link"
                            size="xs"
                            icon={RefreshCwIcon}
                            state={isRefreshLoading ? 'loading' : 'default'}
                            spinOnLoading
                            onClick={handleRefreshConnection}
                        />
                    </div>
                )}
            </div>

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </div>
    );
};

export default ProviderCard;
